/**
 * Hook for saving and loading exoplanet filter preferences.
 */
import { apiService } from '../../../services/apiService';

/**
 * Save current exoplanet filter preferences (fire-and-forget).
 * @param {Object} params
 * @param {Object} params.filters - Filter state
 * @param {Object} params.telescopeConfig - Telescope configuration
 * @param {number} params.baselineBefore - Minutes before transit
 * @param {number} params.baselineAfter - Minutes after transit
 * @param {number} params.minAltitude - Minimum altitude for review
 * @param {boolean} params.showOnlyFullTransits - Full transit filter flag
 */
export const saveExoplanetPreferences = ({
  filters,
  telescopeConfig,
  baselineBefore,
  baselineAfter,
  minAltitude,
  showOnlyFullTransits,
}) => {
  const prefs = {
    hours: filters.hours,
    minDepth: filters.minDepth,
    maxVMag: filters.maxVMag,
    minAltitude: filters.minAltitude,
    enableMoonFilter: filters.enableMoonFilter,
    moonPeakSeparation: filters.moonPeakSeparation,
    moonLorentzianWidth: filters.moonLorentzianWidth,
    selectedFilter: telescopeConfig.selectedFilter,
    binning: telescopeConfig.binning,
    defaultExposure: telescopeConfig.defaultExposure,
    baselineBefore,
    baselineAfter,
    reviewMinAltitude: minAltitude,
    showOnlyFullTransits,
  };
  apiService.updatePreferences({ exoplanet_filters: prefs }).catch(err => {
    console.error('Failed to save exoplanet preferences:', err);
  });
};

/**
 * Load saved exoplanet preferences from user profile and apply them.
 * @param {Object} params
 * @param {Array} availableFilters - Available filter names from observatory
 * @param {Function} setFilters - Filter state setter
 * @param {Function} setTelescopeConfig - Telescope config state setter
 * @param {Function} setBaselineBefore
 * @param {Function} setBaselineAfter
 * @param {Function} setMinAltitude
 * @param {Function} setShowOnlyFullTransits
 */
export const loadSavedPreferences = async ({
  availableFilters,
  setFilters,
  setTelescopeConfig,
  setBaselineBefore,
  setBaselineAfter,
  setMinAltitude,
  setShowOnlyFullTransits,
}) => {
  try {
    const profileResponse = await apiService.getProfile();
    const savedPrefs = profileResponse.data?.user?.preferences?.exoplanet_filters;
    if (!savedPrefs) return;

    console.log('Restoring saved exoplanet preferences:', savedPrefs);

    setFilters(prev => ({
      ...prev,
      hours: savedPrefs.hours ?? prev.hours,
      minDepth: savedPrefs.minDepth ?? prev.minDepth,
      maxVMag: savedPrefs.maxVMag ?? prev.maxVMag,
      minAltitude: savedPrefs.minAltitude ?? prev.minAltitude,
      enableMoonFilter: savedPrefs.enableMoonFilter ?? prev.enableMoonFilter,
      moonPeakSeparation: savedPrefs.moonPeakSeparation ?? prev.moonPeakSeparation,
      moonLorentzianWidth: savedPrefs.moonLorentzianWidth ?? prev.moonLorentzianWidth,
    }));

    if (savedPrefs.selectedFilter && availableFilters.includes(savedPrefs.selectedFilter)) {
      setTelescopeConfig(prev => ({
        ...prev,
        selectedFilter: savedPrefs.selectedFilter,
        binning: savedPrefs.binning ?? prev.binning,
        defaultExposure: savedPrefs.defaultExposure ?? prev.defaultExposure,
      }));
    } else if (savedPrefs.binning || savedPrefs.defaultExposure) {
      setTelescopeConfig(prev => ({
        ...prev,
        binning: savedPrefs.binning ?? prev.binning,
        defaultExposure: savedPrefs.defaultExposure ?? prev.defaultExposure,
      }));
    }

    if (savedPrefs.baselineBefore != null) setBaselineBefore(savedPrefs.baselineBefore);
    if (savedPrefs.baselineAfter != null) setBaselineAfter(savedPrefs.baselineAfter);
    if (savedPrefs.reviewMinAltitude != null) setMinAltitude(savedPrefs.reviewMinAltitude);
    if (savedPrefs.showOnlyFullTransits != null) setShowOnlyFullTransits(savedPrefs.showOnlyFullTransits);
  } catch (prefError) {
    console.error('Failed to load user preferences:', prefError);
  }
};
